import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';
import { companyInfo, projects } from '../mock';
import { FeaturedProjects } from '../components/FeaturedProjects';
import { Collaborations } from '../components/Collaborations';

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  
  useEffect(() => {
    // Set page-specific meta tags for homepage
    document.title = 'Fryd! - Vi realiserer arkitektens vision | Tømrer & Snedkerfirma';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Hos Fryd! bygger vi ikke for hvem som helst. Vi forener dansk kulturarv med håndværk, præcision og kompromisløs kvalitet i træ.');
    }
    
    // Set canonical URL for homepage
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://ffryd.dk/');
  }, []);
  
  // Hero images - using all 3 uploaded images
  const heroImages = [
    {
      id: 1,
      image: "https://customer-assets.emergentagent.com/job_crafted-timber/artifacts/qdkg8r51_Tolvkanten_Juni05_FotoHampusBerndtson_high.jpg",
      title: "Tolvkanten"
    },
    {
      id: 2,
      image: "https://customer-assets.emergentagent.com/job_crafted-timber/artifacts/eeqlh4rq_Tolvkanten_Juni11_FotoHampusBerndtson_high.jpg",
      title: "Tolvkanten Interior"
    },
    {
      id: 3,
      image: "https://customer-assets.emergentagent.com/job_crafted-timber/artifacts/3qpq2shf_Tolvkanten_Juni16_FotoHampusBerndtson_high.jpg",
      title: "Tolvkanten Details"
    }
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section - Full Screen Image Slider */}
      <section className="relative h-screen w-full overflow-hidden">
        {/* Image Slider */}
        {heroImages.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
          </div>
        ))}

        {/* Hero Content */}
        <div className="absolute inset-0 flex flex-col justify-end pb-20 px-8 md:px-16">
          <div className="max-w-6xl">
            <h1 className="text-[#f5f1e8] text-5xl md:text-7xl lg:text-8xl font-light mb-6 tracking-tight">
              {companyInfo.name}
            </h1>
            <p className="text-[#f5f1e8]/90 text-xl md:text-2xl lg:text-3xl font-light mb-10 max-w-3xl leading-relaxed">
              {companyInfo.tagline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#f5f1e8] text-gray-900 text-lg font-light tracking-wide hover:bg-white transition-all duration-300"
              >
                Fortæl os om dit projekt
              </Link>
              <Link
                to="/projekter"
                className="inline-flex items-center space-x-3 text-[#f5f1e8] text-lg font-light border-b border-[#f5f1e8]/60 pb-2 hover:border-[#f5f1e8] transition-all duration-300 group"
              >
                <span>Se vores projekter</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FeaturedProjects />
      <Collaborations />

      {/* Contact Section - Simplified */}
      <section className="py-32 px-8 md:px-16 bg-neutral-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-20">
            {/* Left - Contact Info */}
            <div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-gray-900 mb-12">
                Kontakt
              </h2>
              
              <div className="space-y-8">
                {/* Owner */}
                <div>
                  <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Ejer</p>
                  <p className="text-gray-900 text-xl md:text-2xl font-light mb-8">{companyInfo.contact.owner}</p>
                </div>

                {/* Phone */}
                <div>
                  <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Telefon</p>
                  <a
                    href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`}
                    className="flex items-center space-x-3 text-gray-900 text-xl md:text-2xl font-light hover:text-gray-600 transition-colors duration-300"
                  >
                    <Phone className="w-5 h-5 text-gray-400" />
                    <span>{companyInfo.contact.phone}</span>
                  </a>
                </div>

                {/* Email */}
                <div>
                  <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Email</p>
                  <a
                    href={`mailto:${companyInfo.contact.email}`}
                    className="flex items-center space-x-3 text-gray-900 text-xl md:text-2xl font-light hover:text-gray-600 transition-colors duration-300"
                  >
                    <Mail className="w-5 h-5 text-gray-400" />
                    <span>{companyInfo.contact.email}</span>
                  </a>
                </div>

                {/* Address */}
                <div>
                  <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Adresse</p>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                    <div className="text-gray-900 text-xl md:text-2xl font-light">
                      <p>{companyInfo.contact.address}</p>
                      <p>{companyInfo.contact.city}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media - Without LinkedIn */}
              <div className="mt-12">
                <p className="text-gray-500 text-sm mb-4 uppercase tracking-wide">Find os på</p>
                <div className="flex space-x-4">
                  <a
                    href={companyInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-300"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5 text-gray-700" />
                  </a>
                  <a
                    href={companyInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-300"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5 text-gray-700" />
                  </a>
                </div>
              </div>
            </div>

            {/* Right - Empty space for balance */}
            <div></div>
          </div>

          {/* Logo */}
          <div className="mt-20 flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_crafted-timber/artifacts/g34rxuid_Udklip.PNG" 
              alt="Fryd! logo" 
              className="w-32 h-32 opacity-10 grayscale"
            />
          </div>

          {/* Footer Note */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-gray-400 text-sm text-center">
              © {new Date().getFullYear()} {companyInfo.name} · Alle rettigheder forbeholdes
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
